/** Dr Reddy's org — intro video between welcome and the quiz. */
export const DR_REDDY_ORG_ID = "d70f3637-1f67-4887-83e5-13e026837aaa";

const NORMALIZED_DR_REDDY_ID = DR_REDDY_ORG_ID.toLowerCase();
const USER_DATA_KEY = "userData";
const SESSION_KEY = "session";

function normalizeId(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function isDrReddyOrgId(value) {
  return normalizeId(value) === NORMALIZED_DR_REDDY_ID;
}

export function isDrReddySession({ organizationId, sessionId } = {}) {
  return isDrReddyOrgId(organizationId) || isDrReddyOrgId(sessionId);
}

export function readStoredUserData() {
  try {
    const raw =
      sessionStorage.getItem(USER_DATA_KEY) ||
      localStorage.getItem(USER_DATA_KEY) ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function readPersistedSessionPair() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const [sessionId, organizationId] = raw.split("&");
    return { sessionId, organizationId };
  } catch {
    return {};
  }
}

function readUrlSessionPair() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    organizationId:
      params.get("organizationId") ||
      params.get("organizationid") ||
      params.get("orgId") ||
      "",
    sessionId: params.get("sessionId") || params.get("sessionid") || "",
  };
}

/** Collect org/session ids from common API field names. */
function pickSessionFields(source) {
  if (!source || typeof source !== "object") return {};
  return {
    organizationId:
      source.organizationId ??
      source.organization_id ??
      source.organizationid ??
      source.orgId,
    sessionId:
      source.sessionId ?? source.session_id ?? source.sessionid,
  };
}

/** Deep scan for the Dr Reddy UUID anywhere in stored user payload. */
function containsDrReddyId(value, depth = 0) {
  if (depth > 4 || value == null) return false;
  if (typeof value === "string") return isDrReddyOrgId(value);
  if (Array.isArray(value)) {
    return value.some((item) => containsDrReddyId(item, depth + 1));
  }
  if (typeof value === "object") {
    return Object.values(value).some((item) =>
      containsDrReddyId(item, depth + 1)
    );
  }
  return false;
}

export function isDrReddyFromStorage() {
  const stored = readStoredUserData();
  return (
    isDrReddySession(pickSessionFields(stored)) ||
    containsDrReddyId(stored)
  );
}

export function resolveIsDrReddy({ storedUser, authUser, sessionSlice } = {}) {
  const stored = storedUser ?? readStoredUserData();
  const persisted = readPersistedSessionPair();
  const fromUrl = readUrlSessionPair();

  const sources = [
    pickSessionFields(stored),
    pickSessionFields(authUser),
    pickSessionFields(sessionSlice),
    persisted,
    fromUrl,
  ];

  if (sources.some(isDrReddySession)) return true;
  if (containsDrReddyId(stored) || containsDrReddyId(authUser)) return true;
  return false;
}

export function parseThemeIntroFlag(value) {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/** Welcome → video when Dr Reddy org or admin enabled intro on this theme. */
export function shouldShowIntroVideo({ themeData, authUser, sessionSlice } = {}) {
  if (resolveIsDrReddy({ authUser, sessionSlice })) return true;
  if (!themeData) return false;
  const introOn = parseThemeIntroFlag(themeData.intro);
  const introFile = String(themeData.introFile ?? "").trim();
  return introOn && introFile.length > 0;
}
